import { useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import LicenseDrawer from '..';
import Locator from '../../../../../shared/Locator';
import { Action } from '../../../../../typings/permissions';
import { useDresser } from '../../../../contexts/DrawerContext';
import { useEditable } from '../../../../hooks';
import useCorrectLicense from '../../../../hooks/api/licenses/useCorrectLicense';
import useHasProjectPermission from '../../../../shared/permissions/useHasProjectPermission';
import Button, { BUTTON_TYPE, ButtonGroup, TooltipButton } from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import TextArea from '../../../generic/Forms/TextArea';
import type { DependencyLicense } from '../../../../../shared/types/licenses';
import type { DependencyProp } from '../types';

import styles from '../LicenseDrawer.module.less';

type Props = {
  revisionLicenseId: number;
  dependency: DependencyProp;
  copyright: DependencyLicense['copyright'];
};

type ModeContentProps = Props & {
  setIsEditing: (isEditing: boolean) => void;
  setCopyright: (copyright: string) => void;
};

const ReadModeCopyrightTab: VoidFunctionComponent<Omit<ModeContentProps, 'revisionLicenseId' | 'setCopyright'>> = ({
  copyright,
  setIsEditing,
  dependency,
}) => {
  const hasPermissionToEdit = useHasProjectPermission(Locator.parsePackageString(dependency.locator), Action.Edit);
  return (
    <>
      <div className={styles.title}>
        <Flex justify="space-between">
          Copyright
          <TooltipButton
            id="edit-copyright"
            onClick={() => setIsEditing(true)}
            isDisabled={!hasPermissionToEdit}
            tooltip="You do not have permission to edit this project."
            placement="left"
            showTooltip={!hasPermissionToEdit}
          >
            Edit
          </TooltipButton>
        </Flex>
      </div>
      <TextArea label="Copyright" value={copyright ?? 'No copyright information available.'} isDisabled />
    </>
  );
};

const EditingModeCopyrightTab: VoidFunctionComponent<ModeContentProps> = ({
  copyright,
  setIsEditing,
  dependency,
  revisionLicenseId,
  setCopyright,
}) => {
  const { replaceDrawer } = useDresser();
  const { data, isDirty, onUpdate, onSave } = useEditable(copyright ?? '');
  const { mutate: correctLicense, data: correctLicenseData } = useCorrectLicense(dependency.locator, revisionLicenseId);

  useEffect(() => {
    if (correctLicenseData?.data?.result && correctLicenseData.data?.result?.id !== revisionLicenseId) {
      replaceDrawer(
        <LicenseDrawer
          scope="dependency"
          revisionLicenseId={correctLicenseData.data.result.id}
          dependency={dependency}
        />,
      );
    }
  }, [correctLicenseData, dependency, replaceDrawer, revisionLicenseId]);

  return (
    <>
      <div className={styles.title}>Editing Copyright</div>
      <TextArea label="Copyright" value={data} onChange={(text) => onUpdate(text)} />
      <Flex justify="space-between" direction="row-reverse">
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              correctLicense({ data: { copyright: data } });
              setCopyright(data);
              onSave();
              setIsEditing(false);
            }}
            isDisabled={!isDirty}
          >
            Save
          </Button>
        </ButtonGroup>
      </Flex>
    </>
  );
};

const CopyrightTab: VoidFunctionComponent<Props> = ({ copyright, revisionLicenseId, dependency }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [copyrightText, setCopyrightText] = useState(copyright);

  return isEditing ? (
    <EditingModeCopyrightTab
      copyright={copyrightText}
      setIsEditing={setIsEditing}
      revisionLicenseId={revisionLicenseId}
      dependency={dependency}
      setCopyright={setCopyrightText}
    />
  ) : (
    <ReadModeCopyrightTab copyright={copyrightText} setIsEditing={setIsEditing} dependency={dependency} />
  );
};

export default CopyrightTab;
